import {
  faCaretSquareLeft,
  faCaretSquareRight
} from '@fortawesome/free-regular-svg-icons';
import {
  faExclamationTriangle,
  faHistory,
  faLink,
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TimelineTicket from 'components/app/support-desk/tickets-preview/TimelineTicket';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import SubtleBadge from 'components/common/SubtleBadge';
import TicketSubjectEditModal from 'components/modals/TicketSubjectEditModal';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Nav,
  OverlayTrigger,
  Row,
  Tab,
  Tooltip
} from 'react-bootstrap';
import TicketsService from 'services/Tickets.service';
import SimpleBar from 'simplebar-react';
import ContinuedTickets from './ContinuedTickets';
import TicketAlerts from './TicketAlerts';
import TicketAppointments from './TicketAppointments';
import TicketAttachments from './TicketAttachments';
import TicketBillableItems from './TicketBillableItems';
import TicketInfo from './TicketInfo';
import TicketTasks from './TicketTasks';

const TicketDetails = forwardRef(
  (
    {
      data,
      refreshData,
      showLeftSide,
      showRightSide,
      setShowLeftSide,
      setShowRightSide
    },
    ref
  ) => {
    const [error, setError] = useState({});
    const [isSuccess, setIsSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showModalSubject, setShowModalSubject] = useState(false);
    const [counters, setCounters] = useState({
      appointments: 0,
      tasks: 0,
      attachments: 0,
      alerts: 0,
      continuedTickets: data.childrenTickets?.length || 0,
      billableItems: 0
    });
    const timeline = useRef();
    const files = useRef();
    const ticketInfo = useRef();

    useImperativeHandle(ref, () => ({
      getNewLogs: () => timeline.current.getNewLogs(),
      getFiles: () => files.current.getFiles(),
      getNotes: () => ticketInfo.current.getNotes()
    }));

    const editSubject = async body => {
      let response = await TicketsService.edit(data.id, body);
      if (response.status) {
        refreshData();
        timeline.current && timeline.current.getNewLogs();
      } else {
        setError(response);
        setShowError(true);
      }
      handleCloseModalSubject();
    };

    const handleCloseModalSubject = () => {
      setShowModalSubject(false);
    };

    const isDisabledForm =
      (data.status && data.status.status == 'COMPLETED') ||
      (data.status && data.status.status == 'CLOSED');

    return (
      <>
        <SuccessRequest
          message="Cliente editado com sucesso"
          showAlert={isSuccess}
          setShowAlert={() => setIsSuccess(false)}
        />
        <FailedRequest
          message={error.description}
          showAlert={showError}
          setShowAlert={() => setShowError(false)}
        />
        <TicketSubjectEditModal
          showModal={showModalSubject}
          data={data.subject}
          handleClose={handleCloseModalSubject}
          handleSubmit={editSubject}
        />
        <Row className="g-3">
          <Col xxl={12}>
            <Card>
              <Flex
                alignItems="center"
                justifyContent="between"
                className="w-100 px-2 py-3"
              >
                <Flex alignItems="center">
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        {showLeftSide ? 'Ocultar ' : 'Expandir '} aba esquerda
                      </Tooltip>
                    }
                  >
                    <Button
                      onClick={() => setShowLeftSide(!showLeftSide)}
                      variant="link"
                      size="sm"
                      className="me-2 text-600"
                    >
                      <FontAwesomeIcon
                        icon={
                          showLeftSide ? faCaretSquareLeft : faCaretSquareRight
                        }
                      />
                    </Button>
                  </OverlayTrigger>

                  <h5 className="me-1">
                    #{data.code} - {data.subject}
                  </h5>

                  {data.parentTicket && (
                    <OverlayTrigger
                      overlay={
                        <Tooltip>Ticket pai: #{data.parentTicket.code}</Tooltip>
                      }
                    >
                      <span className="me-2">
                        <FontAwesomeIcon icon={faLink} size="sm" />
                      </span>
                    </OverlayTrigger>
                  )}

                  <Button
                    size="sm"
                    variant="link"
                    className="text-600"
                    onClick={() => setShowModalSubject(true)}
                  >
                    <FontAwesomeIcon icon={faPencilAlt} size="sm" />
                  </Button>
                  <SoftBadge
                    style={{
                      backgroundColor: data.status ? data.status.color : ''
                    }}
                  >
                    {data.status ? data.status.description : ''}
                  </SoftBadge>
                </Flex>

                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      {showRightSide ? 'Ocultar ' : 'Expandir '} aba direita
                    </Tooltip>
                  }
                >
                  <Button
                    onClick={() => setShowRightSide(!showRightSide)}
                    variant="link"
                    className="text-600"
                    size="sm"
                  >
                    <FontAwesomeIcon
                      icon={
                        showRightSide ? faCaretSquareRight : faCaretSquareLeft
                      }
                    />
                  </Button>
                </OverlayTrigger>
              </Flex>
              <Tab.Container defaultActiveKey="basicData">
                <SimpleBar>
                  <Card.Header className="p-0 border-bottom">
                    <Nav className="nav-tabs border-0 flex-nowrap overflow-auto tab-contact-details">
                      <Nav.Item>
                        <Nav.Link
                          eventKey="basicData"
                          className="d-flex align-items-center py-3 mb-0 text-nowrap"
                        >
                          <FontAwesomeIcon
                            icon="stream"
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">Detalhes</h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="appointments"
                          className="d-flex align-items-center py-3 mb-0 text-nowrap"
                        >
                          <FontAwesomeIcon icon="clock" className="text-600" />
                          <h6 className="text-600 mb-0 ms-1">
                            Apontamentos {`(${counters.appointments})`}
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="tasks"
                          className="d-flex align-items-center py-3 mb-0 text-nowrap"
                        >
                          <FontAwesomeIcon icon="tasks" />
                          <h6 className="text-600 mb-0 ms-1 me-2">
                            Tarefas {`(${counters.tasks})`}
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="billableItems"
                          className="d-flex align-items-center py-3 mb-0 text-nowrap"
                        >
                          <FontAwesomeIcon
                            icon="shopping-cart"
                            className="text-600"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            Itens {`(${counters.billableItems})`}
                            <SubtleBadge bg="primary" className="ms-1">
                              BETA
                            </SubtleBadge>
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="attachments"
                          className="d-flex align-items-center py-3 mb-0 text-nowrap"
                        >
                          <FontAwesomeIcon
                            icon="paperclip"
                            className="text-600"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            Anexos {`(${counters.attachments})`}
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      {counters.continuedTickets > 0 && (
                        <Nav.Item>
                          <Nav.Link
                            eventKey="continuedTickets"
                            className="d-flex align-items-center py-3 mb-0 text-nowrap"
                          >
                            <FontAwesomeIcon
                              icon="ticket-alt"
                              className="text-600"
                            />
                            <h6 className="text-600 mb-0 ms-1">
                              <span className="me-1">Tickets Cont.</span>
                              {`(${counters.continuedTickets})`}
                            </h6>
                          </Nav.Link>
                        </Nav.Item>
                      )}
                      {counters.alerts > 0 && (
                        <Nav.Item>
                          <Nav.Link
                            eventKey="alerts"
                            className="d-flex align-items-center py-3 mb-0 text-nowrap"
                          >
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              className="text-600"
                            />
                            <h6 className="text-600 mb-0 ms-1">
                              Alertas {`(${counters.alerts})`}
                            </h6>
                          </Nav.Link>
                        </Nav.Item>
                      )}
                      <Nav.Item>
                        <Nav.Link
                          eventKey="history"
                          className="d-flex align-items-center py-3 mb-0 text-nowrap"
                        >
                          <FontAwesomeIcon
                            icon={faHistory}
                            className="text-600"
                          />
                          <h6 className="text-600 mb-0 ms-1">Atividades</h6>
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Card.Header>
                </SimpleBar>
                <Card.Body>
                  <Tab.Content>
                    <Tab.Pane eventKey="basicData">
                      <TicketInfo
                        data={data}
                        refreshData={refreshData}
                        refreshLogs={() => timeline.current.getNewLogs()}
                        ref={ticketInfo}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="appointments">
                      <TicketAppointments
                        ticketStatus={data.status && data.status.status}
                        ticketId={data.id}
                        refreshLogs={() => timeline.current.getNewLogs()}
                        refreshData={refreshData}
                        onCountChange={count =>
                          setCounters(prev => ({
                            ...prev,
                            appointments: count
                          }))
                        }
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="tasks">
                      <TicketTasks
                        ticketId={data.id}
                        ticketStatus={data.status && data.status.status}
                        refreshLogs={() => timeline.current.getNewLogs()}
                        onCountChange={count =>
                          setCounters(prev => ({ ...prev, tasks: count }))
                        }
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="attachments">
                      <TicketAttachments
                        ref={files}
                        ticketId={data.id}
                        ticketStatus={data.status && data.status.status}
                        refreshLogs={() => timeline.current.getNewLogs()}
                        onCountChange={count =>
                          setCounters(prev => ({ ...prev, attachments: count }))
                        }
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="continuedTickets">
                      <ContinuedTickets
                        continuedTickets={data.childrenTickets}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="alerts">
                      <TicketAlerts
                        ticketId={data.id}
                        onCountChange={count =>
                          setCounters(prev => ({ ...prev, alerts: count }))
                        }
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="billableItems">
                      <TicketBillableItems
                        ticketId={data.id}
                        ticketStatus={data.status && data.status.status}
                        refreshLogs={() => timeline.current.getNewLogs()}
                        onCountChange={count =>
                          setCounters(prev => ({
                            ...prev,
                            billableItems: count
                          }))
                        }
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="history">
                      <TimelineTicket ticketId={data.id} ref={timeline} />
                    </Tab.Pane>
                  </Tab.Content>
                </Card.Body>
              </Tab.Container>
            </Card>
          </Col>
        </Row>
      </>
    );
  }
);

export default TicketDetails;
